import type { PropertyListItemResDTO, Property, PropertyDetailResDTO, PropertyDetail } from "@/types/api"

// Transform API property to frontend property
export function transformApiProperty(apiProperty: PropertyListItemResDTO | any): Property {
  // Handle different possible coordinate structures
  let coordinates: [number, number] = [0, 0]
  
  // Debug: Log the actual structure we're receiving
  console.log('API Property structure:', {
    id: apiProperty.id,
    coordinates: apiProperty.coordinates,
    hasLongitude: (apiProperty as any).longitude !== undefined,
    hasLatitude: (apiProperty as any).latitude !== undefined,
    longitude: (apiProperty as any).longitude,
    latitude: (apiProperty as any).latitude
  })
  
  try {
    if (apiProperty.coordinates) {
      // New nested structure
      coordinates = [apiProperty.coordinates.longitude, apiProperty.coordinates.latitude]
      console.log('Using nested coordinates:', coordinates)
    } else if ((apiProperty as any).longitude !== undefined && (apiProperty as any).latitude !== undefined) {
      // Old flat structure
      coordinates = [(apiProperty as any).longitude, (apiProperty as any).latitude]
      console.log('Using flat coordinates:', coordinates)
    } else {
      console.warn('No coordinates found for property:', apiProperty.id)
    }
  } catch (error) {
    console.warn('Error processing coordinates for property:', apiProperty.id, error)
    coordinates = [0, 0]
  }

  // Handle different image structures
  let imageUrl = "/placeholder.svg?height=250&width=400"
  let imageUrls = ["/placeholder.svg?height=250&width=400"]
  
  if (apiProperty.image && typeof apiProperty.image === 'object' && apiProperty.image.url) {
    // New structure: { id, url, order }
    imageUrl = apiProperty.image.url
    imageUrls = [apiProperty.image.url]
  } else if (apiProperty.images && Array.isArray(apiProperty.images)) {
    // Old structure: string[]
    imageUrl = apiProperty.images[0] || "/placeholder.svg?height=250&width=400"
    imageUrls = apiProperty.images.length ? apiProperty.images : ["/placeholder.svg?height=250&width=400"]
  }

  return {
    id: apiProperty.id,
    name: apiProperty.name,
    price: apiProperty.price,
    location: apiProperty.cityName,
    neighborhood: apiProperty.locationName || apiProperty.residenceName,
    address: apiProperty.address || `${apiProperty.locationName || apiProperty.residenceName}, ${apiProperty.cityName}`,
    bathrooms: apiProperty.bathrooms,
    roommates: apiProperty.roommates,
    diningRooms: apiProperty.diningrooms || apiProperty.diningRooms,
    area: 0,
    image: imageUrl,
    images: imageUrls,
    coordinates,
    description: apiProperty.description || apiProperty.name,
    longDescription: apiProperty.description || apiProperty.name,
    amenities: [],
    includes: [],
    rules: [],
    isFeatured: apiProperty.isFeatured,
    isAvailable: true,
    residenceId: apiProperty.residenceId || 0,
  }
}

// Transform API property detail to frontend property detail
export function transformApiPropertyDetail(apiProperty: PropertyDetailResDTO): PropertyDetail & { faqs?: Array<{question: string, answer: string}> } {
  return {
    id: apiProperty.id,
    name: apiProperty.name,
    price: apiProperty.price,
    location: apiProperty.cityDescription,
    neighborhood: apiProperty.locationName,
    address: apiProperty.address,
    bathrooms: apiProperty.bathrooms,
    roommates: apiProperty.roommates,
    diningRooms: apiProperty.diningrooms,
    coordinates: [apiProperty.longitude, apiProperty.latitude] as [number, number],
    description: apiProperty.description,
    images: apiProperty.images.map((img) => img.url), // ONLY property images
    residenceImages: apiProperty.residenceImages.map((img) => img.url), // ONLY residence images
    virtualTour: apiProperty.virtualTour,
    minimumStay: apiProperty.minimunMonthStayRequired,
    isFeatured: apiProperty.isFeatured,
    residenceId: apiProperty.residenceId,
    residenceDescription: apiProperty.residenceDescription,
    // Add property image IDs for deletion tracking
    propertyImageIds: apiProperty.images.map((img) => img.id),
    amenities: apiProperty.amenities || [],
    features: apiProperty.features || [],
    rules: apiProperty.rules || [],
    faqs: apiProperty.faqs || [],
  }
}

// Transform multiple API properties
export function transformApiProperties(apiProperties: PropertyListItemResDTO[]): Property[] {
  return apiProperties.map(transformApiProperty)
}

// Price formatting utility
export function formatPrice(price: number): string {
  return `€${price.toLocaleString("es-ES")}`
}

// Location formatting utility
export function formatLocation(location: string, neighborhood?: string): string {
  if (neighborhood && neighborhood !== location) {
    return `${location}, ${neighborhood}`
  }
  return location
}
